import { isValidElement, type ReactNode } from "react";
import { Link } from "@/router";
import type { ButtonTheme } from "@/components/Button";
import Icon from "@/components/Icon";
import IconButton from "@/components/IconButton";
import type { Params, Path } from "@/router";
import { LinkProps } from "@generouted/react-router/client";
import Menu, { MenuItem } from "@/components/Menu";
import {
  ActionCluster,
  consolidateActions,
  DEFAULT_MAX_INLINE_ACTIONS,
  MENU_ONLY_CLUSTER_LABEL,
  type Action
} from "@/components/MenuButton";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import classNames from "classnames";

export default function TopAppBar<P extends Path>({
  backLink,
  onBackClick,
  cancelButton,
  actions,
  clusterActions,
  extendedActions = [],
  stickyActions = false
}: {
  backLink?: LinkProps<P, Params>;
  /** When set, runs instead of navigating via `backLink`; `backLink` still supplies the label. */
  onBackClick?: () => void;
  cancelButton?: React.ReactNode;
  actions?: React.ReactNode;
  clusterActions?: Action[];
  extendedActions?: MenuItem[];
  stickyActions?: boolean;
}) {
  const menuItems: MenuItem[] = [];
  const { isCompactLayout } = useIsCompactLayout();

  if (Array.isArray(actions)) {
    actions.forEach((action) => {
      menuItems.unshift({ label: action.props.children, onClick: action.props.onClick });
    });
  }

  const hasCluster = Boolean(clusterActions && clusterActions.length > 0);
  const foldedArrayActions = isCompactLayout && hasCluster ? compactArrayActions(actions) : undefined;
  const resolvedClusterActions = hasCluster ? [...(clusterActions ?? []), ...(foldedArrayActions ?? [])] : undefined;
  const clusterMaxInline = isCompactLayout ? 1 : undefined;
  const clusterConsolidation = resolvedClusterActions
    ? consolidateActions({
        actions: resolvedClusterActions,
        maxInline: clusterMaxInline ?? DEFAULT_MAX_INLINE_ACTIONS
      })
    : null;
  const clusterMenuButtonLabel =
    isCompactLayout &&
    clusterConsolidation &&
    clusterConsolidation.inlineActions.length === 0 &&
    clusterConsolidation.menuItems.length > 0
      ? MENU_ONLY_CLUSTER_LABEL
      : undefined;
  const clusteredActions =
    resolvedClusterActions && resolvedClusterActions.length > 0 ? (
      <ActionCluster
        actions={resolvedClusterActions}
        maxInline={clusterMaxInline}
        menuButtonLabel={clusterMenuButtonLabel}
      />
    ) : null;
  const compactLegacyMenu = isCompactLayout && menuItems.length > 1 && !hasCluster;

  return (
    <div
      className={classNames(
        "px-3 border-b md:-ml-4 md:px-12 pt-7 pb-2",
        // z-[49] matches TopActionBar so sticky page content (e.g. PersonObjectHeading at z-30) scrolls under this bar
        stickyActions ? "sticky z-[49] top-0 bg-sys-brand-surface" : ""
      )}
      data-testid="top-app-bar"
    >
      <div className="w-full flex items-center h-[70px] justify-between">
        {backLink &&
          !cancelButton &&
          (onBackClick ? (
            <button type="button" onClick={onBackClick} className="flex items-center" data-testid="back">
              <Icon name="arrow_back" size={24} className="text-sys-brand-on-surface m-2" />
              <span className="text-title-large">{backLink.title || "Back"}</span>
            </button>
          ) : (
            <Link {...backLink}>
              <div className="flex items-center">
                <IconButton name="arrow_back" data-testid="back" iconClassName="text-sys-brand-on-surface" />
                <span className="text-title-large">{backLink.title || "Back"}</span>
              </div>
            </Link>
          ))}
        {cancelButton}
        {compactLegacyMenu ? (
          <div className="flex items-center gap-2">
            <Menu
              AnchorComponent={<IconButton name="pending" data-testid="menu-actions-button" />}
              menuItems={menuItems.concat(extendedActions)}
            />
          </div>
        ) : (
          <div className="flex items-center">
            <div className="flex items-center gap-2">
              {clusteredActions}
              {foldedArrayActions ? null : actions}
            </div>
            {extendedActions.length > 0 && (
              <Menu
                AnchorComponent={<IconButton name="pending" data-testid="extended-actions-button" />}
                menuItems={extendedActions || []}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** Converts every JS-array action, or returns undefined so the original nodes stay visible. */
function compactArrayActions(actions: ReactNode): Action[] | undefined {
  if (!Array.isArray(actions) || actions.length === 0) return undefined;

  const converted: Action[] = [];
  for (const node of actions) {
    const action = toClusterAction(node);
    if (!action) return undefined;
    converted.push(action);
  }
  return converted;
}

function isButtonTheme(theme: unknown): theme is ButtonTheme {
  return (
    theme === "dark" ||
    theme === "light" ||
    theme === "error" ||
    theme === "warn" ||
    theme === "info" ||
    theme === "pgaAssistant"
  );
}

function toClusterAction(node: unknown): Action | undefined {
  if (
    !isValidElement<{
      children?: ReactNode;
      onClick?: () => void;
      variant?: string;
      disabled?: boolean;
      loading?: boolean;
      loadingLabel?: string;
      testId?: string;
      icon?: unknown;
      svgIcon?: ReactNode;
      trailingIcon?: boolean;
      theme?: unknown;
      iconClassName?: string;
    }>(node)
  ) {
    return undefined;
  }

  const text = actionLabel(node.props.children);
  if (!text) return undefined;

  const { icon, svgIcon, trailingIcon, theme, iconClassName } = node.props;
  if (svgIcon != null || trailingIcon) return undefined;
  if (icon != null && typeof icon !== "string") return undefined;

  const action: Action = { text };
  if (node.props.onClick) action.onClick = node.props.onClick;
  if (node.props.variant === "filled" || node.props.variant === "outlined" || node.props.variant === "text") {
    action.variant = node.props.variant;
  }
  if (node.props.disabled) action.disabled = node.props.disabled;
  if (node.props.loading) {
    action.loading = true;
    action.disabled = true;
  }
  const foldedLoadingLabel = node.props.loadingLabel?.trim();
  if (foldedLoadingLabel) action.loadingLabel = foldedLoadingLabel;
  if (typeof icon === "string") action.iconName = icon as Action["iconName"];
  if (isButtonTheme(theme)) action.theme = theme;
  if (iconClassName) action.iconClassName = iconClassName;
  if (node.props.testId) action.testId = node.props.testId;
  return action;
}

function actionLabel(children: ReactNode): string {
  if (typeof children === "string" || typeof children === "number") return String(children);
  return "";
}
